/**
 * @linkhr/hr-domain/payslip-line — Phase 05.2 Plan 13 Risk #3 boundary.
 *
 * The backend's `GET /api/v1/me/payslips` returns a typed `Page` whose
 * `content?: ListItem[]` was nailed down by CC-50/CC-52 (downgraded the
 * audit Risk #3 from Critical to Warning). The remaining drift surface is
 * runtime-only: `ListItem.status` is typed as plain `string` in the
 * generated schema, so backend can rotate the enum without bumping the
 * contract. We defend with a Zod schema at the hook boundary.
 *
 * Behaviour (per AUDIT-2026-04-27 §"Risk #3 — Recommended mitigation"):
 *
 *   1. Each row goes through `safePayslipParse(row)`. Returns either
 *      `{ ok: true, data }` or `{ ok: false, fallback, issues }` — does
 *      NOT throw.
 *   2. On failure, log a Sentry breadcrumb (`payslip-shape-drift`) and
 *      a `console.warn` so production telemetry surfaces the drift. The
 *      row STILL renders, with the one safe fallback there is
 *      (`status="DRAFT"`). Users are not blocked. There is deliberately NO
 *      currency fallback — see the field.
 *   3. `status` uses `z.enum(["DRAFT","APPROVED","PAID"]).catch("DRAFT")`
 *      so unknown enum values coerce to `DRAFT` rather than failing the
 *      whole row.
 *
 * Tests live next door — `payslip-line.test.ts`. Per project conventions Testing
 * Philosophy this Zod schema qualifies as MUST-test (data-transformation
 * business logic at a security/correctness boundary).
 */

import { z } from "zod";
import { PAYOUT_METHODS } from "./payout";

/**
 * Mirrors `components["schemas"]["ListItem"]` from the generated OpenAPI
 * schema. Every field is optional because the upstream contract marks
 * them all optional — we treat `status` as the strict-defended field
 * (enum + `.catch`) since that is the documented runtime drift vector.
 */
/**
 * Server-derived flags from CC-79 (be-086). Renders as chips in the salary
 * register Flag column. Treated as a free-form `string[]` at the schema
 * boundary so unknown future flags pass through without breaking the row;
 * the rendering layer matches against the known set and falls back to the
 * raw code as label for anything else.
 */
export const KNOWN_PAYSLIP_FLAGS = [
  "OVERTIME",
  "BONUS",
  "LOAN_ACTIVE",
  "ABSENCE_DEDUCTION",
  "MISSING_BANK",
  "EOS_OVERRIDDEN",
  // CC-174 — zero-salary payslip flag.
  "ZERO_SALARY",
  // CC-470 — CliQ payout with no identifier on file (a pay-step data gap, like MISSING_BANK).
  "MISSING_CLIQ_IDENTIFIER",
] as const;
export type KnownPayslipFlag = (typeof KNOWN_PAYSLIP_FLAGS)[number];

export const PayslipLineView = z.object({
  // Backend can emit any optional field as either `null` or `undefined`
  // depending on the source row (PAID with snapshotted maps vs un-computed
  // DRAFT vs pre-CC-79 legacy rows). Accept both — downstream consumers
  // guard `value !== undefined && value !== null` uniformly.
  id: z.string().uuid().nullable().optional(),
  runId: z.string().uuid().nullable().optional(),
  runType: z.string().nullable().optional(),
  /** The owning run's lifecycle — same value as `status` on the employee view. */
  runStatus: z
    .enum(["DRAFT", "COMPUTING", "COMPUTED", "FINALIZING", "FINALIZED", "PAID", "VOIDED"])
    .nullable()
    .optional(),
  periodYear: z.number().int().min(2000).max(2100).nullable().optional(),
  periodMonth: z.number().int().min(1).max(12).nullable().optional(),
  periodLabel: z.string().nullable().optional(),
  netPay: z.number().nullable().optional(),
  // CC-299 (be-CC299) — server-computed per-line roll-ups. `grossTotal` is the
  // sum of all earnings, `deductionsTotal` the sum of all deductions; both are
  // authoritative (they account for rounding the FE map-sum would miss).
  // Nullable on legacy/uncomputed rows — consumers fall back to summing the maps.
  grossTotal: z.number().nullable().optional(),
  deductionsTotal: z.number().nullable().optional(),
  // The end-of-service award, and the total that actually reconciles to net.
  // `grossTotal` deliberately keeps meaning WAGE gross — it feeds YTD earnings,
  // salary reconciliation, reports and the bank writer's zero-salary rule, so
  // folding the award into it would make every termination month read as a
  // salary-cost spike. The award is a discrete `EOS_AWARD` row in `earnings`,
  // and `payableEarningsTotal` is the sum a payslip has to add up to:
  //   payableEarningsTotal = grossTotal + eosAmount
  //   netPay               = payableEarningsTotal - deductionsTotal
  // Reconciling an itemized breakdown against `grossTotal` instead of this
  // produces a negative residual of exactly the award. Null on rows computed
  // before the split; consumers fall back to `grossTotal`.
  eosAmount: z.number().nullable().optional(),
  payableEarningsTotal: z.number().nullable().optional(),
  // CC-299 — employer-side social-security contribution for this line. Employer
  // cost, NOT an employee deduction; rendered in its own section on the detail
  // breakdown. Null on legacy rows.
  employerSscAmount: z.number().nullable().optional(),
  // No default. A parse boundary must never invent a currency: a defaulted
  // "JOD" is indistinguishable downstream from a payslip the backend really
  // denominated in dinars, so every formatter past this point would confidently
  // print the wrong money for a Saudi tenant. `EmployeePayslipLine.currency` is
  // required in the contract, so this is unreachable against a healthy backend
  // — which is the point: if the contract ever slips, the amount renders
  // without a currency rather than claiming dinars.
  currency: z.string().nullish(),
  // The RUN's lifecycle, which is what a payslip line's status actually is.
  // This used to read `["DRAFT","APPROVED","PAID"]` — "APPROVED" is not a value
  // the server has ever sent, and the three real mid-states were missing. So
  // every FINALIZED payslip (the ordinary case) failed the enum and `.catch`
  // coerced it to DRAFT: employees saw a "Draft" badge on payslips that were
  // finished, and any rule keyed on the public states would have matched none
  // of them. The mocks emitted the same fiction, which is why nothing caught it.
  status: z
    .enum(["DRAFT", "COMPUTING", "COMPUTED", "FINALIZING", "FINALIZED", "PAID", "VOIDED"])
    .catch("DRAFT"),
  payslipAvailable: z.boolean().nullable().optional(),
  // CC-300 — the run's paid timestamp (date-time). Present once the run is PAID;
  // null/undefined on DRAFT/APPROVED rows. Surfaced as the "Pay date" column.
  depositDate: z.string().nullable().optional(),
  // CC-79 (be-086) — additive: per-component breakdown maps + server-derived flags.
  earnings: z.record(z.string(), z.number()).nullable().optional(),
  deductions: z.record(z.string(), z.number()).nullable().optional(),
  flags: z.array(z.string()).nullable().optional(),
  // CC-204 (be-362, closes fe-194) — frozen bilingual labels for tenant-defined
  // component codes (allowances, recurring/one-time deductions). Keyed by the
  // same code as the earnings/deductions maps; system roll-ups (BASE,
  // SS_DEDUCTION, …) are absent here and stay FE-localized. Never null on the
  // wire (empty map), but kept optional for pre-CC-204 cached rows.
  componentLabels: z
    .record(z.string(), z.object({ labelEn: z.string(), labelAr: z.string() }))
    .nullable()
    .optional(),
  // CC-116 (be-169) — employee identity snapshot fields. departmentName is a
  // LIVE join (current department, not snapshot per be-169 §1).
  employeeId: z.string().uuid().nullable().optional(),
  employeeNumber: z.string().nullable().optional(),
  displayName: z.string().nullable().optional(),
  departmentName: z.string().nullable().optional(),
  // CC-66 / D-03 — per-line adjustment deltas. Present on ADJUSTMENT-run lines:
  // `deltaNet` is the payable/recoverable difference vs the origin line,
  // `originNet` the origin line's net, `originLineId` the FK back to it. Null on
  // REGULAR runs. For finance the delta IS the amount to pay or claw back.
  deltaNet: z.number().nullable().optional(),
  originNet: z.number().nullable().optional(),
  originLineId: z.string().nullable().optional(),
  // Phase 26 / CC-142-PREVIEW (be-207) — cost-center snapshot at compute time.
  // Populated from employee.primary_cost_center_id at payroll run compute; null
  // for employees with no cost center assigned. Additive — all pre-Phase-26
  // rows have these as undefined/null which PayslipListTable renders as "—".
  costCenterId: z.string().uuid().nullable().optional(),
  costCenterCode: z.string().nullable().optional(),
  // CC-288/CC-289 (be-533/be-534) — mid-month hire/leaver proration. base +
  // fixed allowances already carry the prorated amount; these three fields
  // explain *why* it's reduced. Null on legacy rows; a full-month line is
  // `1.0 / <days> / <days>` (proratedDays === daysInPeriod).
  prorationFactor: z.number().nullable().optional(),
  proratedDays: z.number().int().nullable().optional(),
  daysInPeriod: z.number().int().nullable().optional(),
  // The run's recorded full-month basic salary, and the figures its leave
  // encashment was priced from. Only the single line read fills them; they are
  // null on register rows, /me/payslips and lines computed before they were kept.
  // `days` and `dailyRate` are unrounded, and `days` is negative on a recovery.
  monthlyBase: z.number().nullable().optional(),
  leaveEncashment: z
    .object({ days: z.number(), dailyRate: z.number(), monthlyBasis: z.number() })
    .nullable()
    .optional(),
  // Days of this period's wages that were already paid ahead of an annual
  // leave, and the coverage rows that say where they went. Present on the
  // REGULAR run that would otherwise have paid them a second time — the
  // amount is EXCLUDED from this line, not deducted from it, so screens must
  // read it as an explanation of a smaller net and never as a deduction row.
  // A leave crossing month-end contributes one entry per month it touches.
  prepaidLeaveDays: z.number().nullable().optional(),
  prepaidLeaveProvenance: z
    .array(
      z.object({
        coverageId: z.string(),
        prepayRunId: z.string(),
        leaveRequestId: z.string(),
        coveredStart: z.string(),
        coveredEnd: z.string(),
        days: z.number().nullable().optional(),
        excludedAmount: z.number().nullable().optional(),
      }),
    )
    .nullable()
    .optional(),
  // CC-181 (be-306) — salary-hold (arrears) state. The backend read-path is a
  // gap as of CC-181 (fe-164), so these stay undefined until it ships `onHold`
  // / `holdReason` on the ListItem; the MSW mock stamps them in dev so the
  // "Held — arrears" badge works. Additive + forward-compatible.
  onHold: z.boolean().nullable().optional(),
  holdReason: z.string().nullable().optional(),
  // CC-183 (be-311) — per-run adjustment read-state denormalized onto the row.
  // `inlineDeductions` lists HR-direct deductions (ftId drives DELETE);
  // `loanInstallment` carries the ids the skip endpoint needs + the skipped flag.
  inlineDeductions: z
    .array(
      z.object({
        ftId: z.string(),
        amount: z.number(),
        reason: z.string().nullable().optional(),
      }),
    )
    .nullable()
    .optional(),
  loanInstallment: z
    .object({
      loanId: z.string(),
      installmentId: z.string(),
      amount: z.number(),
      skipped: z.boolean(),
    })
    .nullable()
    .optional(),
  // CC-470 — how this line was paid, frozen onto the payslip at compute time.
  // `payoutDestinationMasked` is the masked bank account / CliQ destination and
  // is null on CASH. Zod STRIPS keys the schema doesn't declare, so both have to
  // be declared here or the payslip surfaces render blank against a populated
  // response. Nullable + optional: legacy rows predate the field.
  payoutMethod: z.enum(PAYOUT_METHODS).nullable().optional(),
  payoutDestinationMasked: z.string().nullable().optional(),
  // Per-account payments for a salary split. Null unless the line was paid into
  // more than one account; `payoutDestinationMasked` stays the primary's.
  // `splitCollapsed` marks a share that rounded to nothing on a tiny net, so
  // the whole net went to the primary this period — a note, not an error.
  payments: z
    .array(
      z.object({
        amount: z.number(),
        destinationMasked: z.string().nullable().optional(),
        transferPercent: z.number(),
        isPrimary: z.boolean(),
      }),
    )
    .nullable()
    .optional(),
  splitCollapsed: z.boolean().nullable().optional(),
  // Correction (ADJUSTMENT) lines only: what the correction settles. `netPay`
  // and the component maps stay the full restated month; this says what was
  // paid before, the difference, and whether it is paid now or asked back.
  // A line computed before the split has priorNet/deltaNet only — the
  // payable/to-recover pair is null there, never zero.
  correction: z
    .object({
      priorNet: z.number(),
      deltaNet: z.number(),
      recoveryOffset: z.number().nullable().optional(),
      payableAmount: z.number().nullable().optional(),
      toRecoverAmount: z.number().nullable().optional(),
      recoveryReceivedAt: z.string().nullable().optional(),
      recoveryReference: z.string().nullable().optional(),
    })
    .nullable()
    .optional(),
});

export type PayslipLineView = z.infer<typeof PayslipLineView>;

export type PayslipCorrection = NonNullable<PayslipLineView["correction"]>;

/**
 * What a correction line settles: paid to the employee now, asked back from
 * them, or — on a line computed before corrections recorded their balance —
 * neither, because that correction paid its whole restated net. Read from the
 * server's payable/to-recover pair, never derived from `deltaNet`: a recovery
 * still owed from an earlier correction is netted in, so the difference alone
 * can point the wrong way.
 */
export type CorrectionOutcome =
  | { kind: "pay"; amount: number }
  | { kind: "recover"; amount: number; receivedAt: string | null; reference: string | null }
  | { kind: "legacy" };

export function correctionOutcome(c: PayslipCorrection): CorrectionOutcome {
  const pay = c.payableAmount ?? null;
  const recover = c.toRecoverAmount ?? null;
  if (pay === null && recover === null) return { kind: "legacy" };
  if (recover !== null && recover > 0) {
    return {
      kind: "recover",
      amount: recover,
      receivedAt: c.recoveryReceivedAt ?? null,
      reference: c.recoveryReference ?? null,
    };
  }
  return { kind: "pay", amount: pay ?? 0 };
}

/**
 * Whether an employee should see a line as a correction: a correction run's
 * line that carries a payment balance. A correction computed before balances
 * were kept paid its whole net, and the server speaks of it to the employee as
 * an ordinary payslip (notifications, YTD), so it reads as one here too.
 */
export function isSettledCorrection(line: PayslipLineView): boolean {
  return (
    line.runType === "ADJUSTMENT" &&
    line.correction != null &&
    correctionOutcome(line.correction).kind !== "legacy"
  );
}

/**
 * Conservative fallback used when the entire row fails Zod parse (i.e. the
 * input is not even an object). Renders period as an em-dash and net as 0
 * so the row layout still occupies a slot in the table.
 */
const FALLBACK: PayslipLineView = {
  status: "DRAFT",
};

type SafeParseResult =
  | { ok: true; data: PayslipLineView }
  | { ok: false; fallback: PayslipLineView; issues: z.ZodIssue[] };

/**
 * Sentry global accessor. The Sentry SDK installs itself on `window.Sentry`
 * during app bootstrap (Phase 0). In test/Node contexts it is undefined,
 * which is fine — we just no-op.
 */
type SentryShim = {
  captureMessage?: (msg: string, ctx?: { level?: string; extra?: unknown }) => void;
};

function captureSentryDrift(issues: z.ZodIssue[], raw: unknown): void {
  if (typeof window === "undefined") return;
  const sentry = (window as unknown as { Sentry?: SentryShim }).Sentry;
  if (sentry?.captureMessage === undefined) return;
  sentry.captureMessage("payslip-shape-drift", {
    level: "warning",
    extra: { issues, raw },
  });
}

/**
 * Validates a single payslip row at the hook boundary. Never throws.
 *
 * Usage at the hook layer:
 *
 *   const validated = (raw.content ?? []).map((row) => {
 *     const r = safePayslipParse(row);
 *     return r.ok ? r.data : r.fallback;
 *   });
 */
/**
 * WAGE gross from the per-component earnings map — i.e. the fallback for a
 * null `grossTotal` roll-up.
 *
 * The `earnings` map INCLUDES `EOS_AWARD` as a display component; the
 * `grossTotal` roll-up this stands in for EXCLUDES it by contract. A bare
 * map-sum therefore changes what "gross" means, and every consumer that later
 * adds `eosAmount` (payable-earnings fallbacks do) counts the award twice — on
 * exactly the payslip where it dominates, a settlement. We subtract the map's
 * OWN entry rather than `eosAmount` so the exclusion is exact even when
 * `eosAmount` is absent, and round to fils precision like every other rail sum.
 */
export function wageGrossFromComponents(
  earnings: Record<string, number> | null | undefined,
): number {
  if (earnings == null) return 0;
  const sum = Object.values(earnings).reduce((a, b) => a + b, 0);
  const award = earnings["EOS_AWARD"] ?? 0;
  return Math.round((sum - award) * 1000) / 1000;
}

/**
 * A rounded row sits at most half a minor unit from its exact sum, whatever the
 * rounding mode. A wider gap means the sum isn't how the row was priced (the
 * calendar-month basis can miss by a fil or two), and printing it would show
 * evidence that doesn't add up.
 */
function reproduces(exact: number, amount: number, currency: string | null | undefined): boolean {
  if (currency == null || currency === "") return false;
  const digits =
    new Intl.NumberFormat("en", { style: "currency", currency }).resolvedOptions()
      .maximumFractionDigits ?? 2;
  return Math.abs(exact - amount) <= 0.5 * 10 ** -digits + 1e-9;
}

type WorkingLine = Pick<
  PayslipLineView,
  | "currency"
  | "earnings"
  | "deductions"
  | "monthlyBase"
  | "daysInPeriod"
  | "proratedDays"
  | "leaveEncashment"
>;

/** How a part-month BASE row was worked out, or null when it can't be shown truthfully. */
export function baseSalaryWorking(
  line: WorkingLine,
): { monthly: number; divisor: number; days: number } | null {
  const { monthlyBase: monthly, daysInPeriod: divisor, proratedDays: days } = line;
  const amount = line.earnings?.["BASE"];
  if (monthly == null || divisor == null || days == null || amount === undefined) return null;
  // A full month has nothing to work out.
  if (divisor <= 0 || days >= divisor) return null;
  return reproduces((monthly / divisor) * days, amount, line.currency)
    ? { monthly, divisor, days }
    : null;
}

/** The server prices a day of leave at the monthly basis over 30, in every country. */
const LEAVE_DAY_DIVISOR = 30;

/**
 * How a settlement's leave row was worked out: encashed leave, or overdrawn leave
 * taken back as a deduction. Null when the line didn't record it.
 */
export function leaveEncashmentWorking(line: WorkingLine): {
  code: "LEAVE_ENCASHMENT" | "LEAVE_ENCASHMENT_RECOVERY";
  days: number;
  dailyRate: number;
  monthlyBasis: number;
  divisor: number;
} | null {
  if (line.leaveEncashment == null) return null;
  const { days, dailyRate, monthlyBasis } = line.leaveEncashment;
  const code = days < 0 ? "LEAVE_ENCASHMENT_RECOVERY" : "LEAVE_ENCASHMENT";
  const amount = days < 0 ? line.deductions?.[code] : line.earnings?.[code];
  if (amount === undefined) return null;
  const adds =
    reproduces(Math.abs(days) * dailyRate, amount, line.currency) &&
    reproduces(monthlyBasis / LEAVE_DAY_DIVISOR, dailyRate, line.currency);
  return adds
    ? { code, days: Math.abs(days), dailyRate, monthlyBasis, divisor: LEAVE_DAY_DIVISOR }
    : null;
}

export function safePayslipParse(row: unknown): SafeParseResult {
  const result = PayslipLineView.safeParse(row);
  if (result.success) return { ok: true, data: result.data };

  // Production telemetry — Sentry breadcrumb + console.warn so the drift
  // is visible in observability without breaking the user. Both side
  // effects are wrapped so test environments without window/Sentry no-op.
  captureSentryDrift(result.error.issues, row);
  if (typeof console !== "undefined" && typeof console.warn === "function") {
    console.warn("[payslip-shape-drift]", { issues: result.error.issues, row });
  }

  return { ok: false, fallback: { ...FALLBACK }, issues: result.error.issues };
}
